import React, { useCallback, useContext, useRef, useState } from "react";
import { FolderName, ProgressCallback, SKMediaType, uploadFile, ErrorCallback } from "../../../api/shared/uploadService";
import { UploadTask } from "firebase/storage";
import * as Yup from "yup";
import { ContentState } from "../../../models/SKArtifact";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";

interface MultiTypeInputProps {
  index: number;
  onFileChange: (index: number, fileUrl: string, fileType: SKMediaType) => void;
  onStart: (index: number) => void;
  docId: string;
  maxFileSize: number;
  imageUrl: string;
  editable?: boolean;
  folder?: FolderName;
}

const MultiTypeInput: React.FC<MultiTypeInputProps> = ({ index = 0, onFileChange, onStart, docId, maxFileSize, imageUrl, editable, folder }) => {
  const [filePreview, setFilePreview] = useState<string | ArrayBuffer | null>(imageUrl);
  const { contentData, updateContentData, artifactId, submit, containerButtonTitle } = useContext(CreateArtifactContext);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [imageTask, setImageTask] = useState<UploadTask | null>(null);
  const [mediaType, setMediaType] = useState<SKMediaType>(SKMediaType.photo);
  const [isLoading, setIsLoading] = useState(false);

  const documentSchema = Yup.object().shape({
    documentFile: Yup.mixed<File>().test("fileSize", `File Size should be below ${maxFileSize} MB`, value => {
      if (!value) return false;
      const fileSize = value.size;
      return fileSize <= maxFileSize * 1024 * 1024;
    }),
  });

  const getAspectRatio = () => {
    if (imagePreview) {
      const img = new Image();
      img.src = imagePreview;
      return img.naturalWidth / img.naturalHeight;
    }
    return 1;
  };

  const getContainerStyle = () => {
    const aspectRatio = getAspectRatio();
    return `h-[${100 / aspectRatio}%]`;
  };

  const successCallback = useCallback(
    (url: string, mType: SKMediaType) => {
      setIsLoading(false);
      onFileChange(index, url, mType);
    },
    [onFileChange]
  );

  const progressCallback: ProgressCallback = (progress: number, mediaType: SKMediaType) => {
    console.log(`Upload progress: ${progress}%`);
  };

  const errorCallback: ErrorCallback = (error: Error, mediaType: SKMediaType) => {
    setIsLoading(false);
    console.error(`Upload failed: ${error.message}`);
  };

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      onStart(index);
      try {
        const fileUrl = URL.createObjectURL(file);
        await documentSchema.validate({ documentFile: file }, { abortEarly: false });
        setErrorMessage("");
        setFilePreview(fileUrl);
        let task = uploadFile(
          file,
          docId,
          file.type.startsWith("image/") ? SKMediaType.photo : SKMediaType.video,
          progressCallback,
          errorCallback,
          successCallback,
          folder
        );
        setImageTask(task);
        setIsLoading(true);
      } catch (err) {
        let error = err as Error;
        setErrorMessage(error.message);
        setIsLoading(false);
      }
    },
    [successCallback, docId, documentSchema]
  );

  return (
    <div id="mediaContainer" className="flex flex-col">
      <div
        className={`flex relative  items-center justify-center p-4 border-2 border-white border-solid min-h-[114px] min-w-[86px] max-w-[86px] max-h-[114px] md:max-w-[150px] md:max-h-[220px] overflow-hidden rounded-lg ${
          filePreview ? getContainerStyle() : "h-[114px] md:h-[220px]"
        }`}
        onClick={() => {
          if (editable && contentData.status !== ContentState.IN_REVIEW) {
            fileInputRef?.current?.click();
          }
        }}
      >
        <input
          key={index}
          name="imageFile"
          type="file"
          ref={fileInputRef}
          accept="image/*,video/*"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${index}`}
          disabled={!editable}
        />

        {/* {uploadingPhoto && (
          <div className="items-center text-center ">
            <p>Uploading...</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setImagePreview(null);
                setUploadingPhoto(false);
                imageTask && imageTask.cancel();
              }}
              className="p-1 text-white bg-black rounded-lg"
            >
              Cancel
            </button>
          </div>
        )} */}
        <label htmlFor="file-upload" className="cursor-pointer content-center h-[114px] md:h-[220px]">
          <div id="showMessage" className="flex flex-col items-center justify-center">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center p-4 space-y-4 bg-white shadow-sm rounded-2xl">
                <img src={"/icons/mediaupload.gif"} alt="Saving" className="w-[25px] h-[25px]" />
                <div className="font-bold text-center text-gray-400 w-[69px] h-[16px]">Uploading...</div>
                <button
                  className="border border-gray-400 text-black rounded-md w-[53px] h-[30px]"
                  onClick={e => {
                    e.stopPropagation();
                    setImagePreview(null);
                    setUploadingPhoto(false);
                    imageTask && imageTask.cancel();
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
              </svg>
            )}
            <span className="text-xs text-center text-skgray">{isLoading ? "" : "Photo or Video"}</span>
          </div>
        </label>
      </div>
      {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};

export default MultiTypeInput;
